import { PlaceKit } from '@placekit/autocomplete-react';
import cx from 'clsx';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import Form from '@placekit/uikit/components/Form';
import FormField from '@placekit/uikit/components/FormField';
import Input from '@placekit/uikit/components/Input';

import { useUIContext } from 'features/UIContext';
import { useT } from 'i18n';
import Button from '@placekit/uikit/components/Button';

const DemoCheckout = (props) => {
  const { notify } = useUIContext();
  const t = useT();
  const [address, setAddress] = useState();

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const onReset = useCallback(() => {
    setAddress();
    methods.reset();
  }, [methods]);

  const onPick = useCallback(
    (_, item) => {
      methods.setValue('line1', item.name);
      methods.setValue('city', item.city);
      methods.setValue('administrative', item.administrative);
      if (item.zipcode[0]) {
        methods.setValue('zipcode', item.zipcode[0]);
      }
      methods.setValue('country', item.country);
    },
    [methods],
  );

  const onGeolocation = useCallback(
    (value, pos, error) => {
      if (error) {
        notify({
          kind: 'danger',
          icon: 'exclamation-triangle',
          delay: 3000,
          title: t('demos.geolocationFailed'),
          message: error,
        });
      }
    },
    [notify, t],
  );

  return (
    <div className={cx('grid lg:grid-cols-3 gap-4 lg:gap-6', props.className)}>
      <p className="lg:order-last max-lg:px-4 lg:pt-6 font-semibold text-sm text-gray-600">
        {t('demos.checkout.intro')}
      </p>
      <div className="lg:col-span-2 p-4 sm:p-6 rounded-lg bg-white border border-gray-200 shadow-md shadow-gray-800/5">
        <div
          className={cx('grid grid-cols-2 gap-4', {
            hidden: !address,
          })}
        >
          <h3 className="col-span-full font-medium text-gray-600">
            {t('demos.checkout.shippingAddress')}
          </h3>
          <address className="col-span-full flex flex-col pl-4 border-l-2 border-gray-100 text-sm not-italic">
            <span className="font-medium">{address?.line1}</span>
            <span>
              {address?.zipcode} {address?.city}
            </span>
            <span>{address?.administrative}</span>
            <span className="uppercase">{address?.country}</span>
          </address>
          <Button
            onClick={() => setAddress()}
            rounded={false}
            label={t('demos.checkout.editShipping')}
          />
          <Button
            onClick={onReset}
            kind="secondary"
            rounded={false}
            label={t('demos.checkout.payment')}
            icon="arrow-long-right"
            iconPosition="right"
          />
        </div>
        <Form
          methods={methods}
          onSubmit={setAddress}
          className={cx('grid grid-cols-2 gap-4 items-end', {
            hidden: !!address,
          })}
        >
          <FormField
            name="line1"
            label={t('demos.checkout.addressLabel')}
            className="col-span-full"
            render={() => (
              <PlaceKit
                name="line1"
                placeholder={t('demos.checkout.addressPlaceholder')}
                className="w-full"
                apiKey={process.env.NEXT_PUBLIC_PLACEKIT_API_KEY}
                options={{
                  types: ['street'],
                }}
                onPick={onPick}
                onGeolocation={onGeolocation}
              />
            )}
          />
          <FormField
            name="city"
            label={t('demos.checkout.cityLabel')}
            className="col-span-full"
            render={({ errored, ...fieldProps }) => <Input {...fieldProps} />}
          />
          <FormField
            name="administrative"
            label={t('demos.checkout.stateLabel')}
            className="col-span-full"
            render={({ errored, ...fieldProps }) => <Input {...fieldProps} />}
          />
          <FormField
            name="zipcode"
            label={t('demos.checkout.postCodeLabel')}
            render={({ errored, ...fieldProps }) => <Input {...fieldProps} />}
          />
          <FormField
            name="country"
            label={t('demos.checkout.countryLabel')}
            render={({ errored, ...fieldProps }) => <Input {...fieldProps} />}
          />
          <Button
            type="submit"
            kind="secondary"
            rounded={false}
            disabled={!methods.watch('line1')}
            label={t('demos.checkout.save')}
            className="col-span-full"
          />
        </Form>
      </div>
    </div>
  );
};

DemoCheckout.propTypes = {
  className: PropTypes.string,
};

export default DemoCheckout;
